import {
    OrderReturnApiCreateInput,
    OrderReturnApiUpdateInput,
    OrderReturnDataFields,
    OrderReturnImageReference,
    OrderReturnLineItem,
    OrderReturnReason,
    OrderReturnShippingDimensions,
    OrderReturnShippingWeight,
    OrderReturnStatus,
    VariantShippingDimensionUnit,
    VariantWeightUnit,
} from "@buildresonance/resonance-lib-entities";
import { array, number, object, ObjectSchema, string } from "yup";

import { transformNaNToNull } from "../numberHelpers";
import { requiredOnlyWhenExistsObjectTransformer } from "../objectHelpers";
import { transformBlankStringToNull } from "../stringHelpers";

import {
    demandHqIdSchema,
    orderIdSchema,
    orderItemIdSchema,
    orderReturnIdSchema,
} from "./demandIds";

export const orderReturnLineItemSchema: ObjectSchema<OrderReturnLineItem> =
    object({
        orderItemId: orderItemIdSchema.required(),
        fulfillmentLineItemId: string().nullable(),
        quantity: number()
            .integer()
            .positive()
            .required()
            .transform(transformNaNToNull),
        returnReason: string()
            .transform(transformBlankStringToNull)
            .oneOf(Object.values(OrderReturnReason))
            .required(),
        returnReasonNote: string().nullable(),
        sku: string().nullable(),
    }).noUnknown();

export const orderReturnImageReferenceSchema: ObjectSchema<OrderReturnImageReference> =
    object({
        s3Key: string().required(),
        fileName: string().nullable(),
    }).noUnknown();

export const orderReturnShippingWeightSchema: ObjectSchema<OrderReturnShippingWeight> =
    object({
        amount: number().positive().required().transform(transformNaNToNull),
        unit: string()
            .transform(transformBlankStringToNull)
            .oneOf(Object.values(VariantWeightUnit))
            .required(),
    }).noUnknown();

export const orderReturnShippingDimensionsSchema: ObjectSchema<OrderReturnShippingDimensions> =
    object({
        length: number().positive().required().transform(transformNaNToNull),
        width: number().positive().required().transform(transformNaNToNull),
        height: number().positive().required().transform(transformNaNToNull),
        unit: string()
            .transform(transformBlankStringToNull)
            .oneOf(Object.values(VariantShippingDimensionUnit))
            .required(),
    }).noUnknown();

export const orderReturnDataFieldsSchema: ObjectSchema<OrderReturnDataFields> =
    object({
        status: string()
            .transform(transformBlankStringToNull)
            .oneOf(Object.values(OrderReturnStatus))
            .required(),
        returnLineItems: array()
            .of(orderReturnLineItemSchema)
            .min(1)
            .required(),
        imageReferences: array().of(orderReturnImageReferenceSchema).nullable(),
        shippingWeight: orderReturnShippingWeightSchema
            .transform(requiredOnlyWhenExistsObjectTransformer)
            .default(undefined)
            .nullable(),
        shippingDimensions: orderReturnShippingDimensionsSchema
            .transform(requiredOnlyWhenExistsObjectTransformer)
            .default(undefined)
            .nullable(),
        shopifyReturnId: string().nullable(),
        sellerCloudRmaId: string().nullable(),
        trackingNumber: string().nullable(),
        customerNote: string().nullable(),
        csNote: string().nullable(),
        requestedAt: string().nullable(),
        approvedAt: string().nullable(),
        refundedAt: string().nullable(),
    }).noUnknown();

export const orderReturnApiCreateInputSchema: ObjectSchema<OrderReturnApiCreateInput> =
    orderReturnDataFieldsSchema
        .concat(
            object({
                demandHqId: demandHqIdSchema.required(),
                orderId: orderIdSchema.required(),
            }),
        )
        .noUnknown();

export const orderReturnApiUpdateInputSchema: ObjectSchema<OrderReturnApiUpdateInput> =
    orderReturnDataFieldsSchema
        .partial()
        .concat(
            object({
                orderReturnId: orderReturnIdSchema.required(),
            }),
        )
        .noUnknown();
